import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

export type ModelStatus =
  | "Training"
  | "Deployed"
  | "Testing"
  | "Production"
  | "Archived";
export type ModelTaskType =
  | "Classification"
  | "Regression"
  | "Embedding"
  | "Sequence model"
  | "LLM / text generation"
  | "Image model"
  | "QC flag prediction";

export interface DeploymentTarget {
  computeType: "GPU" | "CPU";
  region: string;
  endpointUrl?: string;
  containerVersion?: string;
}

export interface FoundryModel {
  id: number;
  name: string;
  version: string;
  status: ModelStatus;
  taskType: ModelTaskType;
  owner: string;
  deploymentTarget?: DeploymentTarget;
  createdAt: string;
  updatedAt: string;
}

export interface ListFoundryModelsResponse {
  data: FoundryModel[];
  total: number;
  pageTotal: number;
  skip: number;
  take: number;
}

export interface CreateFoundryModelPayload {
  name: string;
  version: string;
  status: ModelStatus;
  taskType: ModelTaskType;
  owner: string;
  deploymentTarget?: DeploymentTarget;
}

// Mock data - using a mutable array for demo purposes
let mockFoundryModels: FoundryModel[] = [
  {
    id: 1,
    name: "RF-BiomarkerPredictor",
    version: "v1.0.0",
    status: "Production",
    taskType: "Regression",
    owner: "Foundry Connect",
    deploymentTarget: {
      computeType: "GPU",
      region: "us-central1",
      endpointUrl:
        "https://api.viafoundry.com/models/rf-biomarker-predictor/v1.0.0",
      containerVersion: "v2.1.0",
    },
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 2,
    name: "ViaCancerSubtyper",
    version: "v1.0.0",
    status: "Production",
    taskType: "Classification",
    owner: "Foundry Connect",
    deploymentTarget: {
      computeType: "GPU",
      region: "us-east1",
      endpointUrl:
        "https://api.viafoundry.com/models/via-cancer-subtyper/v1.0.0",
      containerVersion: "v1.8.3",
    },
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

// Function to add a new Foundry Model to mock data
export const addFoundryModelToMockData = (
  payload: CreateFoundryModelPayload
): FoundryModel => {
  const newId = Math.max(...mockFoundryModels.map(m => m.id), 0) + 1;
  const newModel: FoundryModel = {
    id: newId,
    name: payload.name,
    version: payload.version,
    status: payload.status,
    taskType: payload.taskType,
    owner: payload.owner,
    deploymentTarget: payload.deploymentTarget,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  mockFoundryModels = [...mockFoundryModels, newModel];
  return newModel;
};

// Function to delete a Foundry Model from mock data
export const deleteFoundryModelFromMockData = (id: number): boolean => {
  const index = mockFoundryModels.findIndex(m => m.id === id);
  if (index === -1) return false;
  mockFoundryModels = mockFoundryModels.filter(m => m.id !== id);
  return true;
};

const fetchFoundryModelsList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter: Array<{ id: string; value: unknown }>
): Promise<ListFoundryModelsResponse> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));

  let filteredData = [...mockFoundryModels];

  // Apply filters
  filter.forEach(filterItem => {
    if (
      filterItem.value === "" ||
      filterItem.value === null ||
      filterItem.value === undefined
    ) {
      return;
    }

    switch (filterItem.id) {
      case "name": {
        const nameFilter = (filterItem.value as string).toLowerCase();
        filteredData = filteredData.filter(model =>
          model.name.toLowerCase().includes(nameFilter)
        );
        break;
      }
      case "status":
        filteredData = filteredData.filter(
          model => model.status === filterItem.value
        );
        break;
      case "taskType":
        filteredData = filteredData.filter(
          model => model.taskType === filterItem.value
        );
        break;
      case "owner": {
        const ownerFilter = (filterItem.value as string).toLowerCase();
        filteredData = filteredData.filter(model =>
          model.owner.toLowerCase().includes(ownerFilter)
        );
        break;
      }
    }
  });

  // Apply sorting
  if (sort && sort.length > 0) {
    const sortField = sort[0].id;
    const sortDesc = sort[0].desc;
    filteredData.sort((a, b) => {
      let aVal: any = a[sortField as keyof FoundryModel];
      let bVal: any = b[sortField as keyof FoundryModel];

      if (sortField === "createdAt" || sortField === "updatedAt") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (aVal < bVal) return sortDesc ? 1 : -1;
      if (aVal > bVal) return sortDesc ? -1 : 1;
      return 0;
    });
  }

  // Apply pagination
  const total = filteredData.length;
  const paginatedData = filteredData.slice(skip, skip + take);

  return {
    data: paginatedData,
    total,
    pageTotal: paginatedData.length,
    skip,
    take,
  };
};

export const useFoundryModelsList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): UseQueryResult<ListFoundryModelsResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/v1/cortex/foundry-models/list", take, skip, sort, filter],
    queryFn: () => fetchFoundryModelsList(take, skip, sort, filter),
    retry: false,
    enabled,
  });
};

export const useCreateFoundryModel = () => {
  const queryClient = useQueryClient();

  return {
    mutate: (payload: CreateFoundryModelPayload) => {
      const newModel = addFoundryModelToMockData(payload);
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/foundry-models/list"],
      });
      return newModel;
    },
  };
};

export const useDeleteFoundryModel = () => {
  const queryClient = useQueryClient();

  return {
    mutate: (id: number) => {
      const success = deleteFoundryModelFromMockData(id);
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/foundry-models/list"],
      });
      return success;
    },
  };
};
